import navigationGenerator from 'applications/web/routing/navigationGenerator';
import AIHelperDocumentChat from 'components/organisms/AIHelper/AIHelperDocumentChat';
import Layout from 'components/organisms/Layout';
import PaywallLockV2 from 'components/organisms/PaywallLockV2';
import WalletNudgesProvider from 'contexts/WalletNudgesProvider';
import useSubscriptionPlan from 'hooks/Subscriptions/useSubscriptionPlan';
import type { FC } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { SubscriptionLockKeysEnum } from 'types/subscriptions.types';
import { isWeb, isExpert } from 'utils/constants';
import WorkspaceGuard from './WorkspaceGuard';
import type { RouteLockType } from 'types/routing.types';
import CustomerDataGuard from './CustomerDataGuard';
import { IconButton } from 'components/atoms';
import { XIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { SubscriptionPlanNameEnum } from 'types/users.types';
import useCustomNavigate from 'hooks/useCustomeNavigate';

// TODO: MOVE THIS ARRAY LIST TO CONSTANT FILE (constant.ts)
const allowedNoWebAccessRoutes = [
  '/settings/personalDetails',
  '/settings/loginMethods',
  '/settings/changePassword',
  '/settings/taxesSettings',
  '/mobile',
  '/subscriptions',
  '/paymentFailed',
  '/experiments',
];
const PrivateRoute: FC<{
  isAuthenticated: boolean;
  children: any;
  lock?: RouteLockType;
}> = ({ isAuthenticated, children, lock }) => {
  const location = useLocation();

  const navigate = useCustomNavigate();

  const { pathname } = location;

  const params = useParams();

  const subscriptionPlan = useSubscriptionPlan();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  const { locks = {} } = subscriptionPlan?.features || {};

  const blockWebAcces =
    locks?.[SubscriptionLockKeysEnum.access_Web as keyof typeof locks] === true;

  if (!allowedNoWebAccessRoutes.includes(pathname) && isWeb && blockWebAcces) {
    return <Navigate to="/subscriptions" />;
  }

  const _lock = typeof lock === 'function' ? lock({ pathname, params }) : lock;

  const isLocked =
    isWeb && locks?.[_lock?.lockKey as keyof typeof locks] === true;

  if (isLocked) {
    return (
      <Layout
        navigationListGenerator={navigationGenerator}
        className="!px-0 !py-0"
      >
        <div className="relative flex items-center justify-center min-h-screen">
          <div className="w-[55%] mx-auto p-8 px-12 bg-white shadow-cardBlur rounded-2xl z-[99] relative">
            <PaywallLockV2
              lockKey={_lock?.lockKey as string}
              title={_lock?.title as string}
              description={_lock?.description as string}
              primaryFeature={_lock?.primaryFeature}
            />

            {subscriptionPlan?.planInternalName ===
              SubscriptionPlanNameEnum.e_invoicing1 ||
            subscriptionPlan?.planInternalName ===
              SubscriptionPlanNameEnum.e_invoicing12 ? (
              <IconButton
                icon={({ className }) => (
                  <XIcon className={classNames('w-4 h-4', className)} />
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/revenues');
                }}
                className={classNames(
                  'absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 z-10',
                )}
              />
            ) : null}
          </div>
        </div>

        {_lock?.backgroundImage ? (
          <div className="fixed inset-0 left-[var(--sidebar-width)] bg-white bg-opacity-20">
            <img
              src={_lock?.backgroundImage}
              alt="background"
              className="absolute opacity-40 top-0 right-0 bottom-0 left-0"
            />
          </div>
        ) : null}
      </Layout>
    );
  }

  return (
    <>
      <WalletNudgesProvider>
        {isExpert ? (
          <WorkspaceGuard>
            <CustomerDataGuard>{children}</CustomerDataGuard>
          </WorkspaceGuard>
        ) : (
          children
        )}
        <AIHelperDocumentChat />
      </WalletNudgesProvider>
    </>
  );
};

export default PrivateRoute;
